import { c as _c } from "react/compiler-runtime";
import { ReactNode, useMemo, useState } from 'react';
import { ChartNoAxesColumn, Info, Star, ChevronDown, Trash2 } from 'lucide-react';
import { ListItem } from '@/comps/list';
import { SearchInputV2 } from '@/comps/search-input';
import { buildFilterKindName, sortFilterKindName } from '@/comps/FilterPicker/utils';
import { KindAliasFn } from '@/contexts/alias';
import { getFilterTooltip } from '@/utils/filter-tooltips';
import { translateSafe, translate } from '@/utils/translations';
import { cn } from '@/utils/shad';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { BasicTooltip } from '@/comps/basic-tooltip';
import { FilterGroup } from '@/api/generated/model';
import { FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS, stopPopoverWheelPropagation } from '@/comps/FilterPicker/filter-content-v2/constants';
const TOOLTIP_CONTENT_CLASS = cn(FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS, 'max-w-sm break-normal! text-wrap wrap-break-word text-start');
const LABEL_CLASS = 'block min-w-0 truncate text-start text-sm font-normal text-foreground';
export type FilterContentV2FieldType = 'all' | 'interactions' | 'conversations' | 'users' | 'user_groups' | 'tags' | 'custom_columns' | 'conversation_custom_columns' | 'custom_aggregates' | 'saved_filters' | 'favorites' | 'recently_used' | 'time';
function sortFieldTypesWithAllFirst(types: FilterContentV2FieldType[]): FilterContentV2FieldType[] {
  if (!types.includes('all')) return types;
  return ['all', ...types.filter(type => type !== 'all')];
}
export type FilterContentV2GranularityOptions = {
  values: string[];
  favoriteValues: Set<string>;
  selectedValue?: string | null;
  onSelect: (granularity: string) => void;
  onToggleFavorite: (granularity: string, isFavorite: boolean) => void;
};
type FieldDropdownListEntry = {
  type: 'kind';
  value: string;
} | {
  type: 'granularity';
  value: string;
} | {
  type: 'savedFilter';
  value: FilterGroup;
};
function getFieldDropdownEntryLabel(entry: FieldDropdownListEntry, alias?: KindAliasFn): string {
  if (entry.type === 'kind') return buildFilterKindName(entry.value, alias);
  if (entry.type === 'savedFilter') return entry.value.name;
  return translate('granularity' as keyof typeof translate, entry.value);
}
function compareFieldDropdownEntries(a: FieldDropdownListEntry, b: FieldDropdownListEntry, alias?: KindAliasFn): number {
  const labelA = getFieldDropdownEntryLabel(a, alias);
  const labelB = getFieldDropdownEntryLabel(b, alias);
  if (labelA > labelB) return 1;
  if (labelA < labelB) return -1;
  return 0;
}
type Props = {
  kinds: string[];
  favoriteTypes: Set<string>;
  selectedKind: string | null;
  onSelectKind: (kind: string) => void;
  onToggleFavorite: (kind: string, isFavorite: boolean) => void;
  filterSearch: string;
  setFilterSearch: (v: string) => void;
  listPrepend?: ReactNode;
  granularityOptions?: FilterContentV2GranularityOptions;
  sortFavoritesFirst?: (kinds: string[]) => string[];
  selectedType: FilterContentV2FieldType;
  availableTypes: FilterContentV2FieldType[];
  onSelectType: (type: FilterContentV2FieldType) => void;
  savedFilterGroups?: FilterGroup[];
  onSelectSavedFilterGroup?: (filterGroup: FilterGroup) => void;
  onDeleteSavedFilterGroup?: (filterGroup: FilterGroup) => void;
  favoriteSavedFilterGroups?: Set<string>;
  onToggleFavoriteSavedFilterGroup?: (filterGroup: FilterGroup, isFavorite: boolean) => void;
  aliasKindName?: KindAliasFn;
};
export function savedFilterGroupKey(filterGroup: FilterGroup): string {
  return String(filterGroup.id ?? filterGroup.name);
}
export function FilterContentV2FieldDropdown(t0) {
  const $ = _c(61);
  const {
    kinds,
    favoriteTypes,
    selectedKind,
    onSelectKind,
    onToggleFavorite,
    filterSearch,
    setFilterSearch,
    listPrepend,
    granularityOptions,
    sortFavoritesFirst,
    selectedType,
    availableTypes,
    onSelectType,
    savedFilterGroups: t1,
    onSelectSavedFilterGroup,
    onDeleteSavedFilterGroup,
    favoriteSavedFilterGroups,
    onToggleFavoriteSavedFilterGroup,
    aliasKindName
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const savedFilterGroups = t2;
  const isTimeTab = selectedType === "time";
  const isSavedFiltersTab = selectedType === "saved_filters";
  const isFavoritesTab = selectedType === "favorites";
  let t3;
  bb0: {
    if (!isSavedFiltersTab && !isFavoritesTab && selectedType !== "all") {
      let t4;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[2] = t4;
      } else {
        t4 = $[2];
      }
      t3 = t4;
      break bb0;
    }
    let groups;
    if ($[3] !== favoriteSavedFilterGroups || $[4] !== filterSearch || $[5] !== isFavoritesTab || $[6] !== savedFilterGroups) {
      const search = filterSearch.toLowerCase();
      groups = savedFilterGroups.filter(filterGroup => filterGroup.name.toLowerCase().includes(search));
      if (isFavoritesTab) {
        let t4;
        if ($[8] !== favoriteSavedFilterGroups) {
          t4 = filterGroup_0 => favoriteSavedFilterGroups?.has(savedFilterGroupKey(filterGroup_0));
          $[8] = favoriteSavedFilterGroups;
          $[9] = t4;
        } else {
          t4 = $[9];
        }
        groups = groups.filter(t4);
      }
      $[3] = favoriteSavedFilterGroups;
      $[4] = filterSearch;
      $[5] = isFavoritesTab;
      $[6] = savedFilterGroups;
      $[7] = groups;
    } else {
      groups = $[7];
    }
    t3 = groups;
  }
  const visibleSavedFilterGroups = t3;
  let t4;
  if ($[10] !== aliasKindName || $[11] !== favoriteSavedFilterGroups || $[12] !== favoriteTypes || $[13] !== granularityOptions?.favoriteValues || $[14] !== granularityOptions?.values || $[15] !== isSavedFiltersTab || $[16] !== isTimeTab || $[17] !== kinds || $[18] !== sortFavoritesFirst || $[19] !== visibleSavedFilterGroups) {
    const entries = [];
    if (!isTimeTab && !isSavedFiltersTab) {
      const sortedKinds = sortFavoritesFirst ? sortFavoritesFirst(kinds) : [...kinds].sort((a, b) => sortFilterKindName(a, b, aliasKindName));
      entries.push(...sortedKinds.map(_temp));
    }
    entries.push(...(granularityOptions?.values ?? []).map(_temp2));
    entries.push(...visibleSavedFilterGroups.map(_temp3));
    let t5;
    if ($[21] !== favoriteSavedFilterGroups || $[22] !== favoriteTypes || $[23] !== granularityOptions?.favoriteValues) {
      t5 = entry => {
        if (entry.type === "kind") {
          return favoriteTypes.has(entry.value);
        }
        if (entry.type === "savedFilter") {
          return favoriteSavedFilterGroups?.has(savedFilterGroupKey(entry.value)) ?? false;
        }
        return granularityOptions?.favoriteValues.has(entry.value) ?? false;
      };
      $[21] = favoriteSavedFilterGroups;
      $[22] = favoriteTypes;
      $[23] = granularityOptions?.favoriteValues;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    const isEntryFavorite = t5;
    let t6;
    if ($[25] !== aliasKindName || $[26] !== isEntryFavorite) {
      t6 = (a_0, b_0) => {
        const aFav = isEntryFavorite(a_0);
        const bFav = isEntryFavorite(b_0);
        if (aFav !== bFav) {
          return aFav ? -1 : 1;
        }
        return compareFieldDropdownEntries(a_0, b_0, aliasKindName);
      };
      $[25] = aliasKindName;
      $[26] = isEntryFavorite;
      $[27] = t6;
    } else {
      t6 = $[27];
    }
    t4 = entries.sort(t6);
    $[10] = aliasKindName;
    $[11] = favoriteSavedFilterGroups;
    $[12] = favoriteTypes;
    $[13] = granularityOptions?.favoriteValues;
    $[14] = granularityOptions?.values;
    $[15] = isSavedFiltersTab;
    $[16] = isTimeTab;
    $[17] = kinds;
    $[18] = sortFavoritesFirst;
    $[19] = visibleSavedFilterGroups;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const listEntries = t4;
  let t5;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = translateSafe("common", "searchEllipsis");
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      container: "h-auto w-full rounded-lg border-0 bg-[#09090B] p-0",
      inner: "gap-2",
      leftIconContainer: "h-4 w-4 rounded-none border-0 p-0",
      leftIcon: "h-4 w-4",
      input: "h-5 bg-transparent py-0 pr-0 pl-0.5 text-sm placeholder:text-white font-normal shadow-none",
      spacer: "hidden"
    };
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== filterSearch || $[31] !== setFilterSearch) {
    t7 = <SearchInputV2 className="px-3" placeholder={t5} search={filterSearch} setSearch={setFilterSearch} searchInputClassNames={t6} />;
    $[30] = filterSearch;
    $[31] = setFilterSearch;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="my-3 border-b border-zinc-800" />;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t10;
  let t9;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChartNoAxesColumn className="h-4 w-4 shrink-0" />;
    t10 = <span className="text-sm text-foreground">Type</span>;
    $[34] = t10;
    $[35] = t9;
  } else {
    t10 = $[34];
    t9 = $[35];
  }
  let t11;
  if ($[36] !== availableTypes || $[37] !== onSelectType || $[38] !== selectedType) {
    t11 = <div className="border-b border-zinc-800 pb-3"><div className="flex items-center gap-2 px-3 py-0">{t9}{t10}<FilterContentV2TypePill selectedType={selectedType} availableTypes={availableTypes} onSelectType={onSelectType} /></div></div>;
    $[36] = availableTypes;
    $[37] = onSelectType;
    $[38] = selectedType;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== t11 || $[41] !== t7) {
    t12 = <div className="flex flex-col pt-3">{t7}{t8}{t11}</div>;
    $[40] = t11;
    $[41] = t7;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== aliasKindName || $[44] !== favoriteSavedFilterGroups || $[45] !== favoriteTypes || $[46] !== granularityOptions || $[47] !== listEntries || $[48] !== onDeleteSavedFilterGroup || $[49] !== onSelectKind || $[50] !== onSelectSavedFilterGroup || $[51] !== onToggleFavorite || $[52] !== onToggleFavoriteSavedFilterGroup || $[53] !== selectedKind) {
    t13 = listEntries.length === 0 ? <ListItem className="group flex min-h-8 cursor-pointer items-center justify-between gap-1 rounded px-2 py-1.5 font-normal">No results found</ListItem> : listEntries.map(entry_0 => {
      if (entry_0.type === "savedFilter") {
        const filterGroup_1 = entry_0.value;
        const isFavorite = favoriteSavedFilterGroups?.has(savedFilterGroupKey(filterGroup_1)) ?? false;
        return <ListItem key={`saved-${filterGroup_1.id ?? filterGroup_1.name}`} className="group flex min-h-8 cursor-pointer items-center justify-between gap-1 rounded px-2 py-1.5 font-normal hover:bg-zinc-800" onClick={() => onSelectSavedFilterGroup?.(filterGroup_1)}><div className="flex min-w-0 flex-1 items-center gap-2"><BasicTooltip text={filterGroup_1.name} side="bottom" classNameContent={TOOLTIP_CONTENT_CLASS}><span className={cn(LABEL_CLASS, "flex-1")}>{filterGroup_1.name}</span></BasicTooltip></div><div className="flex shrink-0 items-center gap-1.5">{onToggleFavoriteSavedFilterGroup && <Star className={cn("shrink-0 opacity-0 transition-opacity group-hover:opacity-100 hover:cursor-pointer", isFavorite ? "fill-foreground text-foreground" : "text-muted-foreground")} size={16} onClick={evt => {
              evt.stopPropagation();
              onToggleFavoriteSavedFilterGroup(filterGroup_1, isFavorite);
            }} />}{onDeleteSavedFilterGroup && <Trash2 className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:cursor-pointer hover:text-foreground" size={16} onClick={evt_0 => {
              evt_0.stopPropagation();
              onDeleteSavedFilterGroup(filterGroup_1);
            }} />}</div></ListItem>;
      }
      if (entry_0.type === "kind") {
        const kind_0 = entry_0.value;
        const itemName = buildFilterKindName(kind_0, aliasKindName);
        const isFavorite_0 = favoriteTypes.has(kind_0);
        const tooltipText = getFilterTooltip(kind_0);
        const isSelected = selectedKind === kind_0;
        return <ListItem key={`kind-${kind_0}`} className={cn("group flex min-h-8 cursor-pointer items-center justify-between gap-1 rounded px-2 py-1.5 font-normal hover:bg-zinc-800", isSelected && "bg-zinc-800")} onClick={() => onSelectKind(kind_0)}><div className="flex min-w-0 flex-1 items-center gap-2"><BasicTooltip text={itemName} side="bottom" classNameContent={TOOLTIP_CONTENT_CLASS}><span className={LABEL_CLASS}>{itemName}</span></BasicTooltip>{tooltipText && <Tooltip delayDuration={300}><TooltipTrigger onClick={_temp4}><Info size={12} className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" /></TooltipTrigger><TooltipContent className={FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS}>{tooltipText}</TooltipContent></Tooltip>}</div><Star className={cn("shrink-0 opacity-0 transition-opacity group-hover:opacity-100 hover:cursor-pointer", isFavorite_0 ? "fill-foreground text-foreground" : "text-muted-foreground")} size={16} onClick={evt_1 => {
            evt_1.stopPropagation();
            onToggleFavorite(kind_0, isFavorite_0);
          }} /></ListItem>;
      }
      const granularity = entry_0.value;
      const itemName_0 = translate("granularity" as keyof typeof translate, granularity);
      const isFavorite_1 = granularityOptions.favoriteValues.has(granularity);
      const isSelected_0 = granularityOptions.selectedValue === granularity;
      return <ListItem key={`granularity-${granularity}`} className={cn("group flex min-h-8 cursor-pointer items-center justify-between gap-1 rounded px-2 py-1.5 font-normal hover:bg-zinc-800", isSelected_0 && "bg-zinc-800")} onClick={() => granularityOptions.onSelect(granularity)}><div className="flex min-w-0 flex-1 items-center gap-2"><BasicTooltip text={itemName_0} side="bottom" classNameContent={TOOLTIP_CONTENT_CLASS}><span className={cn(LABEL_CLASS, "flex-1")}>{itemName_0}</span></BasicTooltip></div><Star className={cn("shrink-0 opacity-0 transition-opacity group-hover:opacity-100 hover:cursor-pointer", isFavorite_1 ? "fill-foreground text-foreground" : "text-muted-foreground")} size={16} onClick={evt_2 => {
          evt_2.stopPropagation();
          granularityOptions.onToggleFavorite(granularity, isFavorite_1);
        }} /></ListItem>;
    });
    $[43] = aliasKindName;
    $[44] = favoriteSavedFilterGroups;
    $[45] = favoriteTypes;
    $[46] = granularityOptions;
    $[47] = listEntries;
    $[48] = onDeleteSavedFilterGroup;
    $[49] = onSelectKind;
    $[50] = onSelectSavedFilterGroup;
    $[51] = onToggleFavorite;
    $[52] = onToggleFavoriteSavedFilterGroup;
    $[53] = selectedKind;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  let t14;
  if ($[55] !== listPrepend || $[56] !== t13) {
    t14 = <div className="scrollbar flex h-72 flex-col gap-0.5 overflow-y-auto pt-1 pr-1 pb-3 pl-1" onWheel={stopPopoverWheelPropagation}>{listPrepend}{t13}</div>;
    $[55] = listPrepend;
    $[56] = t13;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  let t15;
  if ($[58] !== t12 || $[59] !== t14) {
    t15 = <div className="flex w-64 flex-col overflow-hidden rounded-md border border-zinc-800 bg-[#09090B] shadow-md">{t12}{t14}</div>;
    $[58] = t12;
    $[59] = t14;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  return t15;
}
function _temp4(e) {
  return e.stopPropagation();
}
function _temp3(value_0) {
  return {
    type: "savedFilter" as const,
    value: value_0
  };
}
function _temp2(value) {
  return {
    type: "granularity" as const,
    value
  };
}
function _temp(kind) {
  return {
    type: "kind" as const,
    value: kind
  };
}
function FilterContentV2TypePill(t0) {
  const $ = _c(23);
  const {
    selectedType,
    availableTypes,
    onSelectType
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== selectedType) {
    t1 = translateSafe("filter_tabs", selectedType);
    $[0] = selectedType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedLabel = t1;
  let t2;
  if ($[2] !== availableTypes) {
    t2 = sortFieldTypesWithAllFirst(availableTypes);
    $[2] = availableTypes;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const sortedTypes = t2;
  let t3;
  if ($[4] !== selectedLabel) {
    t3 = <span className="max-w-full truncate">{selectedLabel}</span>;
    $[4] = selectedLabel;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronDown className="size-4 shrink-0" />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3) {
    t5 = <PopoverTrigger asChild={true}><button type="button" className="ml-auto flex h-7 max-w-full min-w-20 items-center justify-between gap-1 rounded-base border border-zinc-800 px-2 text-xs font-semibold">{t3}{t4}</button></PopoverTrigger>;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = cn(FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS, "w-50 border-zinc-800 bg-[#09090B] p-1");
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== onSelectType || $[11] !== selectedType || $[12] !== sortedTypes) {
    let t8;
    if ($[14] !== onSelectType || $[15] !== selectedType) {
      t8 = type => {
        const isSelected = type === selectedType;
        return <ListItem key={type} className={cn("flex min-h-8 cursor-pointer items-center gap-2 rounded px-2 py-1.5 font-normal hover:bg-zinc-800", isSelected && "bg-zinc-800")} onClick={() => {
          onSelectType(type);
          setOpen(false);
        }}><span className="truncate text-sm font-normal text-foreground">{translateSafe("filter_tabs", type)}</span></ListItem>;
      };
      $[14] = onSelectType;
      $[15] = selectedType;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    t7 = sortedTypes.map(t8);
    $[10] = onSelectType;
    $[11] = selectedType;
    $[12] = sortedTypes;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <PopoverContent align="end" className={t6}><div className="scrollbar flex max-h-55 flex-col gap-0.5 overflow-y-auto">{t7}</div></PopoverContent>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== open || $[20] !== t5 || $[21] !== t8) {
    t9 = <Popover open={open} onOpenChange={setOpen}>{t5}{t8}</Popover>;
    $[19] = open;
    $[20] = t5;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}